<template>
  <!-- Calendar Controls -->
  <v-row class="px-4 fill-height">
    <v-col>
      <v-sheet tile height="64" class="d-flex">
        <v-toolbar flat class="transparent">
          <v-btn icon class="ma-2" @click="$refs.calendar.prev()">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn outlined color="grey darken-2" @click="setToday"> {{ $t('TimeTable.today')}}</v-btn>
          <v-btn icon class="ma-2" @click="$refs.calendar.next()">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
          <v-toolbar-title v-if="$refs.calendar">{{ $refs.calendar.title }}</v-toolbar-title>
          <v-spacer/>
          <v-select
            v-model="type"
            :items="types"
            item-text="text"
            item-value="value"
            dense
            outlined
            hide-details
            class="ma-2 flex-grow-0 flex-shrink-1"
            :label="$t('TimeTable.type')"></v-select>
          <v-select
            v-if="type === 'week'"
            v-model="weekday"
            :items="weekdays"
            item-text="text"
            item-value="value"
            dense
            outlined
            hide-details
            class="ma-2 flex-grow-0 flex-shrink-1"
            :label="$t('TimeTable.weekdays')"></v-select>
        </v-toolbar>
      </v-sheet>
      <v-spacer></v-spacer>
      <!-- Calendar -->
      <v-sheet :height="getHeightBasedOnType" class="mt-2">
        <v-calendar
          ref="calendar"
          v-model="value"
          :weekdays="weekday"
          :type="type"
          :events="timetable"
          event-name='description'
          event-start='startTime'
          event-end='endTime'
          interval-height="90"
          :first-interval="firstInterval"
          :interval-count="intervalCount"
          interval-minutes="60"
          :locale="$i18n.locale"
          :event-color="getEventColor"
          @click:event='setExercise'
        >
          <template v-slot:day-body="{ date, week }">
            <div class="v-current-time"
                 :class="{ first: date === week[0].date }"
                 :style="{ top: nowY }"></div>
          </template>
        </v-calendar>
      </v-sheet>
    </v-col>
  </v-row>
</template>

<script>
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { mapState, mapGetters } from 'vuex';
import BusinessLogicOverview from '@/libs/businessLogicOverview';

dayjs.extend(isoWeek);
dayjs.extend(utc);
dayjs.extend(tz);

export default {
  name: 'TimeTable',
  props: {
    isOverview: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      type: 'day',
      intervalCount: 24,
      types: [
        { text: this.$t('TimeTable.types.month'), value: 'month' },
        { text: this.$t('TimeTable.types.week'), value: 'week' },
        { text: this.$t('TimeTable.types.day'), value: 'day' },
      ],
      ready: false,
      weekday: [1, 2, 3, 4, 5],
      weekdays: [
        { text: this.$t('TimeTable.weekdayFormat.Sun-Sat'), value: [0, 1, 2, 3, 4, 5, 6] },
        { text: this.$t('TimeTable.weekdayFormat.Mon-Sun'), value: [1, 2, 3, 4, 5, 6, 0] },
        { text: this.$t('TimeTable.weekdayFormat.Mon-Fri'), value: [1, 2, 3, 4, 5] },
        { text: this.$t('TimeTable.weekdayFormat.MoMiFr'), value: [1, 3, 5] },
      ],
      value: '',
    }
  },
  mounted() {
    this.ready = true;
    this.scrollToTime()
    this.updateTime()
  },
  computed: {
    ...mapGetters(['isExerciseBlocked']),
    ...mapState({
      candidate: (state) => state.run.candidate,
      timetable: (state) => state.run.timetable,
    }),
    firstInterval() {
      if (!Number.isNaN(Number(this.candidate?.earliestEventHour))) {
        const firstInterval = Number(this.candidate.earliestEventHour) - 1
        this.setIntervalCount( 24 - firstInterval)
        return firstInterval;
      }
      const firstInterval = 7;
      this.setIntervalCount(24 - firstInterval);
      return firstInterval;
    },
    events() {
      return this.timetable || [];
    },
    getHeightBasedOnType() {
      switch ( this.type ) {
        case 'month':
          return 600
        case 'day':
        case 'week':
        default:
          return 600
      }
    },
    cal() {
      return this.ready ? this.$refs.calendar : null
    },
    nowY() {
      return this.cal ? `${this.cal.timeToY(this.cal.times.now)}px` : '-10px'
    }
  },
  watch: {
    type(oldType, newType) {
        if (oldType !== newType) this.scrollToTime();
    }
  },
  methods: {
    setExercise({ event }) {
      const logic = new BusinessLogicOverview(this);
      logic.setExercise(event.exerciseId)
    },
    setIntervalCount(int) {
      this.intervalCount = int;
    },
    getCurrentTime() {
      return this.cal ? this.cal.times.now.hour * 60 + this.cal.times.now.minute : 0
    },
    scrollToTime() {
      const time = this.getCurrentTime();
      const first = Math.max(0, time - (time % 30) - 30);
      this.cal.scrollToTime(first)
    },
    updateTime() {
      setInterval(() => this.cal.updateTimes(), 60 * 1000)
    },
    viewDay({ date }) {
      this.value = date;
      this.type = 'day';
    },
    setToday() {
      this.value = ''
    },
    getEventColor(event) {
      return event.color;
    }
  }
}
</script>

<style lang="scss" scoped>
.v-current-time {
  height:2px;
  background-color: var(--v-primary-base);
  position: absolute;
  left: -1px;
  right:0;
  pointer-events: none;

  &.first::before {
    content: '';
    position: absolute;
    background-color: var(--v-primary-base);
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-top: -5px;
    margin-left: -6.5px;
  }
}
</style>
